<template>
    <div class="relative">
        <section v-if="quote">
            <RecordSection
                v-if="insights && quote.notes?.data?.length && isEmployee"
                :title="$t('Header Notes')"
                :record="quote"
                class="mb-8"
                :class="{
                    'm-8 mt-0': !fromDialog,
                    'mt-8': fromDialog,
                }"
            >
                <TableNew
                    :header-component="null"
                    :columns="notesColumns"
                    :rows="quote.notes?.data"
                    :row-component="TableRowTwo"
                    :fixed-first-column="true"
                    :edge-padding-num="fromDialog ? 0 : 6"
                />
            </RecordSection>
            <RecordSection 
                :title="$t('Items')" 
                :record="quote" 
                class="mb-8"
                :class="{
                    'm-8 mt-0': !fromDialog && settings.is_sourcing_mode,
                    'mt-8': fromDialog,
                }"
            >
                <template #buttons>
                    <button v-if="quote.status.toLowerCase() !== 'processing'" @click="exportQuoteLines(quote)" :disabled="isDownloading" class="btn-black-on-gray-pill">
                        <BaseSvg
                            v-if="!isDownloading"
                            name="icon-file-download"
                            class="mr-px w-4"
                            aria-hidden="true"
                        />
                        <LoadingCircle v-else class="w-4 h-4" />
                        {{ $t("CSV") }}
                    </button>
                </template>
                <TableNew
                    ref="items"
                    :columns="columns"
                    :rows="computedRows"
                    :selectable="((props.quotesSelectable && !quote?.source_code?.startsWith('QOC') && !(quote.status.toLowerCase() === 'processing')) ? selectable : false)"
                    :sort="sort"
                    :row-component="markRaw(TableRowTwo)"
                    :edge-padding-num="fromDialog ? 0 : 6"
                />
            </RecordSection>
            <footer
                class="w-full flex items-center justify-between pt-3"
                v-if="!insights"
                :class="{
                    'px-8 !pt-0': settings.is_sourcing_mode,
                }"
            >
                <template v-if="quotesSelectable && !quote?.source_code?.startsWith('QOC')">
                    <AppButton
                        v-if="settings.is_sourcing_mode"
                        :disabled="!addToCartEnabled || inert || (quote.status.toLowerCase() === 'processing')"
                        @click="addToCart"
                        text="Add to cart"
                        theme="contrast"
                        size="large"
                        class="print:hidden"
                        icon="icon-shopping-cart"
                    />

                    <button v-else @click="addToCart" :disabled="!addToCartEnabled || inert || (quote.status.toLowerCase() === 'processing')" class="btn-white-on-black capitalize rounded-full print:hidden">
                        {{ $t("Add to cart") }}
                    </button>
                </template>

                <template v-else-if="quotesSelectable && quote?.source_code?.startsWith('QOC')">
                    <button @click="() => eventBus.emit('chat:showNewMessage', 'Convert quote #'+quote.id)" class="btn-white-on-black capitalize rounded-full print:hidden">
                        {{ $t("Live chat to convert this quote") }}
                    </button>
                </template>

                <div class="grow flex items-end gap-2 justify-end" v-if="!settings.is_sourcing_mode">
                    {{ $t("Quote Total") }}
                    <span class="text-2xl font-bold"><span class="type-secondary">$</span>{{ showMeTheMoney(String(quote.total), false, false) }}</span>
                </div>

            </footer>

            <div
                class="text-sm type-secondary italic"
                :class="{
                    'm-8 mt-4': !fromDialog && settings.is_sourcing_mode,
                    'mt-4': fromDialog,
                }"
                v-if="quote.status.toLowerCase() === 'processing'"
            >
                {{ $t("Once the quote is processed, you will be able to add it to your cart.") }}
            </div>
        </section>
    </div>
</template>

<script setup>
import {computed, inject, markRaw, ref} from "vue";
import {
    RecordSection,
    TableNew,
    TableRowTwo,
    AppButton,
    BaseSvg,
    LoadingCircle,
} from "@/Components";
import {eventBus, useCart, useDownload, useHelpers, useUser} from "@/Composables";
import {useForm} from "@inertiajs/vue3";
import _ from 'lodash';

const props = defineProps({
    quote: {
        type: Object,
        required: true,
    },
    rows: {
        type: Array,
        required: true,
    },
    data: {
        type: Object,
        required: false,
        default: {}
    },
    quotesSelectable: {
        type: Boolean,
        required: false,
        default: true
    },
    sort: {
        type: Object,
        required: false,
        default: {}
    },
    insights: {
        type: Boolean,
        default: false
    },
    notesColumns: {
        type: Array,
        required: false,
        default: []
    },
    cards: {
        type: Object,
        required: false,
        default: {}
    },
    fromDialog: {
        type: Boolean,
        required: false,
        default: false
    },
    columns: {
        type: Array,
        required: false,
        default: []
    },
    fromSales: {
        type: Boolean,
        required: false,
        default: false
    }
});

const sort = ref(props.sort);
const { settings } = useUser();
const { showMeTheMoney } = useHelpers();
const { exportQuoteLines, isDownloading } = useDownload();
const items = ref(null);
const route = inject('route');
const { isEmployee } = useUser();
const inert = ref(false);

const addItemsForm = useForm({
    lines: [],
});

const addToCartEnabled = computed(() => {
    return props.quote?.source_code === 'QOC' || items.value?.selectedRows.length > 0;
});

const addToCart = () => {
    // Prevent action if button should be disabled
    if (!addToCartEnabled.value || inert.value || (props.quote.status.toLowerCase() === 'processing')) {
        return;
    }
    
    addItemsForm.lines = _.map(items.value.selectedRows, 'id');
    addItemsForm.submit('post', route('web.account.sales.quotes.add_to_cart', props.quote.id), {
        only: ['referer', 'errors','flash'],
        onSuccess: (response) => {
            if (response.props.flash.status?.cart_count) {
                useCart().setCartCount(response.props.flash.status.cart_count);
            }
        }
    });
};

const computedRows = computed(() => {
    if (!sort.value) {
        return props.rows
    }
    let orderBy = Object.keys(sort.value)[0];
    let direction = sort.value[orderBy] === 'asc' ? 'asc' : 'desc';
    return _.orderBy(props.rows, [orderBy], [direction]);
})

function selectable(row){
    return row.price > 0 && !row.item_id.startsWith('@');
}

</script>
